import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  canCancel,
  canReschedule,
  useBookingCancellation,
} from '../../../../hooks/booking/useBookingCancellation'

import { DropdownMenuItem } from '../../../dropdownMenu/DropdownMenu'
import { RowActions } from '../../../listItem/rowActions/RowActions'
import { Tabs } from '../../../tabs/Tabs'
import { ListRow } from '../../../listItem/listRow/ListRow'
import Icon from '../../../ui/Icon'
import { ListCard } from '../../../listItem/listCard/ListCard'
import { ListingDetails } from '../../../listing/ListingDetails'
import {
  BookingStatusBadge,
  bookingStatusStripe,
  isBookingVoided,
} from '../BookingStatusBadge'
import {
  BookingAmount,
  BookingFundingIcon,
  useBookingFundingLabel,
} from '../BookingFundingBadge'

import { CONFIG } from '../../../../config'
import { DisplayType } from '../../../../types/listing'
import {
  Booking,
  BookingFilter,
  BookingStatus,
} from '../../../../types/booking/booking'
import {
  checkoutPath,
  formatMinutes,
  timeOf,
} from '../../../../utils/booking/booking'
import { toDisplayOnlyDate } from '../../../../utils/dateUtils'

type BookingHistoryListProps = {
  tab: BookingFilter
  onTabChange: (tab: BookingFilter) => void
}

export const BookingHistoryList = ({
  tab,
  onTabChange,
}: BookingHistoryListProps) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['history', 'bookings'])
  const { confirmCancel } = useBookingCancellation('/history')
  const fundingLabel = useBookingFundingLabel()

  const tabs = Object.values(BookingFilter).map(filter => ({
    value: filter,
    label: t(`history.booking-history-list.filter.${filter.toLowerCase()}`),
  }))

  const showActionsColumn =
    tab === BookingFilter.ALL || tab === BookingFilter.UPCOMING

  /** A session still waiting for its payment: the customer can go back to the checkout and pay. */
  const pendingCheckout = (item: Booking) =>
    item.status === BookingStatus.PENDING_PAYMENT && item.checkoutId
      ? item.checkoutId
      : undefined

  const openCheckout = (item: Booking) => {
    const checkoutId = pendingCheckout(item)
    if (checkoutId) {
      navigate(checkoutPath(checkoutId))
    }
  }

  /** Actions the customer can take on a booking, offered in its "more actions" menu. */
  const menuItems = (item: Booking): DropdownMenuItem[] => [
    ...(pendingCheckout(item)
      ? [
          {
            key: 'pay',
            label: t('history.booking-history-list.pay-btn'),
            icon: 'arrowR',
            onClick: () => openCheckout(item),
          },
        ]
      : []),
    ...(canReschedule(item)
      ? [
          {
            key: 'reschedule',
            label: t('history.booking-history-list.reschedule-btn'),
            icon: 'calendar',
            onClick: () =>
              navigate(`/history/reschedule/${item.id}`, {
                state: { booking: item },
              }),
          },
        ]
      : []),
    ...(canCancel(item)
      ? [
          {
            key: 'cancel',
            label: t('history.booking-history-list.cancel-btn'),
            icon: 'x',
            danger: true,
            onClick: () => confirmCancel(item),
          },
        ]
      : []),
  ]

  /** Every row renders the actions cell (empty when there is nothing to do), so all rows keep the same dividers. */
  const renderActions = (item: Booking) => (
    <RowActions label={t('history.booking-history-list.more-actions')} items={menuItems(item)} />
  )


  const renderReschedules = (item: Booking) =>
    t('history.booking-history-list.reschedules', {
      used: item.rescheduleCount,
      max: item.maxReschedules,
    })

  const renderBooking = (item: Booking, viewMode: DisplayType) => {
    const date = toDisplayOnlyDate(item.startTime, i18n.language)
    const time = `${timeOf(item.startTime)}–${timeOf(item.endTime)}`
    const status = <BookingStatusBadge booking={item} />
    const funding = <BookingFundingIcon booking={item} />
    const amount = <BookingAmount booking={item} />
    const duration = formatMinutes(item.durationMinutes)

    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={item.id}
          onClick={pendingCheckout(item) ? () => openCheckout(item) : undefined}
          title={
            pendingCheckout(item)
              ? t('history.booking-history-list.pay-hint')
              : undefined
          }
          cells={[
            { key: 'date', className: 'mono text-center', content: date },
            { key: 'time', className: 'mono text-center', content: time },
            {
              key: 'court',
              className: 'fw-semibold text-nowrap text-center',
              content: item.courtName,
            },
            {
              key: 'duration',
              className: 'mono text-nowrap text-center',
              content: duration,
            },
            { key: 'funding', className: 'text-center', content: funding },
            { key: 'amount', className: 'text-center', content: amount },
            {
              key: 'reschedules',
              className: 'text-center',
              content: renderReschedules(item),
            },
            { key: 'status', className: 'text-center', content: status },
          ]}
          actions={showActionsColumn ? renderActions(item) : undefined}
        />
      )
    }

    return (
      <ListCard
        key={item.id}
        stripe={bookingStatusStripe(item)}
        title={item.courtName}
        subtitle={
          <>
            <Icon name="calendar" size={12} />
            <span>{`${date} · ${time}`}</span>
          </>
        }
        status={status}
        menu={renderActions(item)}
        muted={isBookingVoided(item)}
        onOpen={pendingCheckout(item) ? () => openCheckout(item) : undefined}
        openLabel={t('history.booking-history-list.pay-hint')}
        fields={[
          {
            key: 'duration',
            label: t('history.booking-history-list.tabel-header.duration'),
            value: duration,
          },
          {
            key: 'funding',
            label: t('history.booking-history-list.tabel-header.funding'),
            value: fundingLabel(item, false),
            mono: false,
          },
          {
            key: 'amount',
            label: t('history.booking-history-list.tabel-header.amount'),
            value: amount,
          },
          {
            key: 'reschedules',
            label: t('history.booking-history-list.tabel-header.reschedules'),
            value: renderReschedules(item),
          },
        ]}
      />
    )
  }

  return (
    <ListingDetails<Booking>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/bookings`}
      initialViewMode={DisplayType.LIST}
      displaySearch
      searchPlaceholder={t('history.booking-history-list.search-placeholder')}
      filter={
        <Tabs
          tab={tab}
          tabs={tabs}
          onChange={value => onTabChange(value as BookingFilter)}
        />
      }
      additionalParams={{ filter: tab }}
      tableColumns={[
        {
          key: 'date',
          label: t('history.booking-history-list.tabel-header.date'),
          align: 'center',
        },
        {
          key: 'time',
          label: t('history.booking-history-list.tabel-header.time'),
          align: 'center',
        },
        {
          key: 'court',
          label: t('history.booking-history-list.tabel-header.court'),
          align: 'center',
        },
        {
          key: 'duration',
          label: t('history.booking-history-list.tabel-header.duration'),
          align: 'center',
        },
        {
          key: 'funding',
          label: t('history.booking-history-list.tabel-header.funding'),
          align: 'center',
        },
        {
          key: 'amount',
          label: t('history.booking-history-list.tabel-header.amount'),
          align: 'center',
        },
        {
          key: 'reschedules',
          label: t('history.booking-history-list.tabel-header.reschedules'),
          align: 'center',
        },
        {
          key: 'status',
          label: t('history.booking-history-list.tabel-header.status'),
          align: 'center',
        },
        ...(showActionsColumn
          ? [
              {
                key: 'actions',
                label: t('history.booking-history-list.tabel-header.actions'),
                align: 'center',
              },
            ]
          : []),
      ]}
      renderItem={renderBooking}
    />
  )
}
